import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion'
import { TrendingUp, TrendingDown, Minus, HelpCircle } from 'lucide-react'

interface StatusCardProps {
  title: string
  value: string | number
  status: 'healthy' | 'warning' | 'critical' | 'unknown' | 'partial' | 'ready' | 'not_ready'
  icon?: React.ReactNode
  subtitle?: string | React.ReactNode
  trend?: 'up' | 'down' | 'stable'
  onClick?: () => void
  tooltip?: string
}

export function StatusCard({ title, value, status, icon, subtitle, trend, onClick, tooltip }: StatusCardProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleTooltipMouseEnter = (e: React.MouseEvent) => {
    if (!tooltip) return;

    const tooltipWidth = 320;
    const tooltipHeight = 150;
    const offset = 12;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Start with mouse position
    let x = e.clientX + offset;
    let y = e.clientY + offset;

    // Check if tooltip would go off the right edge
    if (x + tooltipWidth > viewportWidth - 10) {
      // Place it to the left of the cursor instead
      x = e.clientX - tooltipWidth - offset;
    }

    // Check if tooltip would go off the bottom edge
    if (y + tooltipHeight > viewportHeight - 10) {
      // Place it above the cursor instead
      y = e.clientY - tooltipHeight - offset;
    }

    // Ensure tooltip doesn't go off the left edge
    if (x < 10) {
      x = 10;
    }

    // Ensure tooltip doesn't go off the top edge
    if (y < 10) {
      y = 10;
    }

    setTooltipPosition({ x, y });
    setShowTooltip(true);
  };

  const handleTooltipMouseLeave = () => {
    setShowTooltip(false);
  };

  const statusColors = {
    healthy: 'border-green-500 bg-green-500/10',
    warning: 'border-orange-500 bg-orange-500/10',
    critical: 'border-red-500 bg-red-500/10',
    unknown: 'border-gray-300 dark:border-slate-600 bg-gray-100/10 dark:bg-slate-800/30',
    partial: 'border-yellow-500 dark:border-yellow-600 bg-yellow-500/10 dark:bg-slate-800/30',
    ready: 'border-green-500 bg-green-500/10',
    not_ready: 'border-red-500 bg-red-500/10'
  }

  const statusDotColors = {
    healthy: 'bg-green-500',
    warning: 'bg-orange-500',
    critical: 'bg-red-500',
    unknown: 'bg-gray-400',
    partial: 'bg-yellow-500',
    ready: 'bg-green-500',
    not_ready: 'bg-red-500'
  }

  const trendIcons = {
    up: <TrendingUp className="w-4 h-4 text-green-500" />,
    down: <TrendingDown className="w-4 h-4 text-red-500" />,
    stable: <Minus className="w-4 h-4 text-gray-500" />,
  }

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        className={`card border-2 ${statusColors[status]} cursor-pointer transition-all relative overflow-hidden h-full`}
        onClick={onClick}
      >
        {status === 'critical' && (
          <motion.div
            className="absolute inset-0 bg-red-500 opacity-10"
            animate={{
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
        <div className="relative">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {icon && (
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700/50">
                  {icon}
                </div>
              )}
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))]">
                  {title}
                </h3>
                {tooltip && (
                  <HelpCircle
                    className="w-4 h-4 cursor-help text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    onMouseEnter={handleTooltipMouseEnter}
                    onMouseLeave={handleTooltipMouseLeave}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {trend && trendIcons[trend]}
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`w-2 h-2 rounded-full ${statusDotColors[status]}`}
              />
            </div>
          </div>
          <p className="text-2xl font-bold text-[rgb(var(--text-primary))]">
            {value}
          </p>
          <div className="text-sm text-[rgb(var(--text-secondary))] mt-2 min-h-[2.5rem]">
            {subtitle || '\u00A0'}
          </div>
        </div>
      </motion.div>

      {/* Render tooltip using portal to escape overflow-hidden */}
      {showTooltip && tooltip && createPortal(
        <div
          className="fixed w-80 z-[9999] pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-600 animate-in fade-in duration-200">
            <div className="text-gray-200 leading-relaxed">
              {tooltip}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default StatusCard;